import { useState, useCallback, useRef } from 'react';
import { useMotionValue } from 'framer-motion';

interface MeasurementPoint {
  id: string;
  x: number;
  y: number;
  timestamp: number;
}

interface MeasurementLine {
  id: string;
  startPoint: MeasurementPoint;
  endPoint: MeasurementPoint;
  distance: number;
  label?: string;
}

interface UseFloorPlanInteractionOptions {
  onHotspotClick?: (hotspotId: string) => void;
  onRoomHighlight?: (roomId: string | null) => void;
  onMeasurementComplete?: (measurement: MeasurementLine) => void;
  maxZoom?: number;
  minZoom?: number;
  floorPlanScale?: number; // meters per percentage point
}

export const useFloorPlanInteraction = (options: UseFloorPlanInteractionOptions = {}) => {
  const {
    onHotspotClick,
    onRoomHighlight,
    onMeasurementComplete,
    maxZoom = 3,
    minZoom = 0.5,
    floorPlanScale = 0.1
  } = options;

  // State
  const [zoom, setZoom] = useState(1);
  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<MeasurementPoint[]>([]);
  const [measurementLines, setMeasurementLines] = useState<MeasurementLine[]>([]);
  const [highlightedRoom, setHighlightedRoom] = useState<string | null>(null);
  const [selectedHotspot, setSelectedHotspot] = useState<string | null>(null);

  // Motion values for pan and zoom
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Zoom functions
  const zoomIn = useCallback(() => {
    const newZoom = Math.min(zoom * 1.2, maxZoom);
    setZoom(newZoom);
    scale.set(newZoom);
  }, [zoom, maxZoom, scale]);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(zoom / 1.2, minZoom);
    setZoom(newZoom);
    scale.set(newZoom);
  }, [zoom, minZoom, scale]);

  const resetView = useCallback(() => {
    setZoom(1);
    scale.set(1);
    x.set(0);
    y.set(0);
  }, [scale, x, y]);

  // Measurement functions
  const addMeasurementPoint = useCallback((clientX: number, clientY: number) => {
    if (!imageRef.current || !measurementMode) return;

    const rect = imageRef.current.getBoundingClientRect();
    const pointX = ((clientX - rect.left) / rect.width) * 100;
    const pointY = ((clientY - rect.top) / rect.height) * 100;

    const newPoint: MeasurementPoint = {
      id: `point-${Date.now()}`,
      x: pointX,
      y: pointY,
      timestamp: Date.now()
    };

    setMeasurementPoints(prev => {
      const newPoints = [...prev, newPoint];
      
      // If we have 2 points, create a measurement line
      if (newPoints.length >= 2) {
        const startPoint = newPoints[newPoints.length - 2];
        const endPoint = newPoints[newPoints.length - 1];
        const distance = calculateDistance(startPoint, endPoint);
        
        const measurementLine: MeasurementLine = {
          id: `line-${Date.now()}`,
          startPoint,
          endPoint,
          distance
        };
        
        setMeasurementLines(prevLines => [...prevLines, measurementLine]);
        onMeasurementComplete?.(measurementLine);
      }
      
      return newPoints;
    });
  }, [measurementMode, onMeasurementComplete, floorPlanScale]);

  const calculateDistance = useCallback((point1: MeasurementPoint, point2: MeasurementPoint) => {
    const dx = Math.abs(point2.x - point1.x);
    const dy = Math.abs(point2.y - point1.y);
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);
    return pixelDistance * floorPlanScale;
  }, [floorPlanScale]);

  const clearMeasurements = useCallback(() => {
    setMeasurementPoints([]);
    setMeasurementLines([]);
  }, []);

  const toggleMeasurementMode = useCallback(() => {
    setMeasurementMode(prev => {
      if (prev) {
        // Exiting measurement mode, clear points
        clearMeasurements();
      }
      return !prev;
    });
  }, [clearMeasurements]);

  // Hotspot functions
  const handleHotspotClick = useCallback((hotspotId: string) => {
    setSelectedHotspot(hotspotId);
    onHotspotClick?.(hotspotId);
  }, [onHotspotClick]);

  const clearHotspotSelection = useCallback(() => {
    setSelectedHotspot(null);
  }, []);

  // Room highlighting functions
  const highlightRoom = useCallback((roomId: string | null) => {
    setHighlightedRoom(roomId);
    onRoomHighlight?.(roomId);
  }, [onRoomHighlight]);

  // Image click handler
  const handleImageClick = useCallback((event: React.MouseEvent<HTMLImageElement>) => {
    if (measurementMode) {
      addMeasurementPoint(event.clientX, event.clientY);
    }
  }, [measurementMode, addMeasurementPoint]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        if (measurementMode) {
          toggleMeasurementMode();
        } else if (selectedHotspot) {
          clearHotspotSelection();
        }
        break;
      case '+':
      case '=':
        event.preventDefault();
        zoomIn();
        break;
      case '-':
        event.preventDefault();
        zoomOut();
        break;
      case '0':
        event.preventDefault();
        resetView();
        break;
      case 'm':
      case 'M':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          toggleMeasurementMode();
        }
        break;
    }
  }, [measurementMode, selectedHotspot, toggleMeasurementMode, clearHotspotSelection, zoomIn, zoomOut, resetView]);

  // Format distance for display
  const formatDistance = useCallback((distance: number) => {
    if (distance < 1) {
      return `${(distance * 100).toFixed(0)}cm`;
    }
    return `${distance.toFixed(1)}m`;
  }, []);

  // Get measurement summary
  const getMeasurementSummary = useCallback(() => {
    const totalDistance = measurementLines.reduce((sum, line) => sum + line.distance, 0);
    return {
      totalPoints: measurementPoints.length,
      totalLines: measurementLines.length,
      totalDistance: formatDistance(totalDistance)
    };
  }, [measurementLines, measurementPoints.length, formatDistance]);

  return {
    // State
    zoom,
    measurementMode,
    measurementPoints,
    measurementLines,
    highlightedRoom,
    selectedHotspot,
    
    // Motion values
    x,
    y,
    scale,
    
    // Refs
    containerRef,
    imageRef,
    
    // Actions
    zoomIn,
    zoomOut,
    resetView,
    toggleMeasurementMode,
    clearMeasurements,
    handleHotspotClick,
    clearHotspotSelection,
    highlightRoom,
    handleImageClick,
    handleKeyDown,
    
    // Utilities
    formatDistance,
    getMeasurementSummary,
    calculateDistance
  };
};